// Apply the planned rewrites to the open query tabs, undoably, and turn the
// per-tab outcome into a `TabCourtesyNotice`.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § NAV1 (amended 2026-08-23).
//
// Two application routes, chosen per tab:
//   • MOUNTED tab (an entry in `renameBufferEditors`) → `executeEdits`, which
//     brackets the splice in `pushUndoStop()` on both sides. One Ctrl+Z in that
//     tab restores the pre-rename text, and Monaco's own change event flushes
//     the new content into the workspace store through `useQueryBufferSync`.
//   • UNMOUNTED tab → `updateTabContent`, the same seam MCP's
//     `ui_set_editor_text` uses. `useQueryBufferSync` seeds its state from the
//     store on mount, so the rewrite is picked up rather than clobbered.
//
// Query history is untouched — that half of NAV1 still stands.

import type { DatabaseEngineId } from '@/core/engines';

import { rangeFromOffsets, buildLineIndex } from '../../sql-grammar/document/lineIndex';
import type {
  QualifiedName,
  RenameDestination,
  RenameKind,
  TabCourtesyNotice,
} from '../types';

import type { BufferEdit } from './bufferRenamePlan';
import {
  getRenameBufferEditor,
  RENAME_TAB_UNDO_SOURCE,
  type RenameBufferEditor,
} from './renameBufferEditors';
import { planTabRewrite } from './tabRewritePlanner';

export type RewritableTab = {
  readonly tabId: string;
  readonly sql: string;
  readonly engineId: DatabaseEngineId;
  /** The tab is bound to a DIFFERENT database than the rename ran in, so only
   *  a reference that SPELLS the renamed database denotes the renamed object
   *  in it — see `tabRewritePlanner`'s `crossDatabase`. Set by
   *  `renamePostApplyTabPass`, which is the layer that knows each tab's
   *  database. */
  readonly crossDatabase?: boolean;
};

/** Per-engine identifier-case settings that decide whether a reference denotes
 *  the renamed object. Absent → `relationIdentity` falls back to
 *  case-INSENSITIVE, which on a case-sensitive server rewrites a DIFFERENT
 *  object, so these must be threaded from the same probes the rename dialog
 *  path uses. */
export type EngineCaseOptions = {
  readonly mysqlLowerCaseTableNames?: number;
  readonly mssqlCatalogCaseSensitive?: boolean;
};

export type RewriteTarget = EngineCaseOptions & {
  /** The connection the rename ran on. `tabRewritePlanner` reads the SQL
   *  Server SERVER collation off it, so a `_CS_` instance keeps
   *  `Sales.dbo.users` and `sales.dbo.users` apart in a buffer the same way
   *  the persisted-body planner does; absent → case-insensitive, as before. */
  readonly connectionId?: string;
  /** The database the rename RAN in. Threaded on to `tabRewritePlanner` as the
   *  effective database every reference is resolved against, so a buffer that
   *  spells ANOTHER database is not rewritten — see
   *  {@link import('./tabRewritePlanner').TabRewriteRequest.database}. */
  readonly database?: string;
  readonly kind: RenameKind;
  readonly fromIdentifier: QualifiedName;
  /** The destination the PLAN settled, carried whole (`renameDestination.ts`).
   *  It replaced a bare `toIdentifier` here: the planner half needs the decoded
   *  name AND the catalog spelling to render a token, and every surface that
   *  held only one of the two ended up re-deriving the other. The notice's
   *  "new name" is `destination.identifier`, the same identity the batch
   *  carries as `target.toIdentifier`. */
  readonly destination: RenameDestination;
  readonly containingTable?: QualifiedName;
};

export type ApplyTabRewritesDeps = {
  /** Live-editor lookup. Defaults to the module registry. */
  readonly getEditor?: (tabId: string) => RenameBufferEditor | null;
  /** Store write for tabs with no live editor. */
  readonly setTabSql: (tabId: string, sql: string) => void;
};

export type ApplyTabRewritesResult = {
  readonly notices: readonly TabCourtesyNotice[];
  readonly rewrittenTabs: number;
};

export function applyTabRewrites(
  target: RewriteTarget,
  tabs: readonly RewritableTab[],
  deps: ApplyTabRewritesDeps,
): ApplyTabRewritesResult {
  const getEditor = deps.getEditor ?? getRenameBufferEditor;
  const notices: TabCourtesyNotice[] = [];
  let rewrittenTabs = 0;

  // A column batch that lost its host relation cannot be proved against a
  // buffer — every candidate tab gets the warning banner rather than a guess.
  if (target.kind === 'column' && !target.containingTable) {
    return {
      notices: tabs.map((tab) => makeNotice(target, tab, 'stale', staleRanges(tab, []))),
      rewrittenTabs: 0,
    };
  }

  for (const tab of tabs) {
    const plan = planTabRewrite({
      sql: tab.sql,
      engineId: tab.engineId,
      // The planner reads the SQL Server SERVER collation off this id, so a
      // `_CS_` instance keeps `Sales.dbo.users` and `sales.dbo.users` apart in
      // the buffer the same way the persisted-body planner does.
      connectionId: target.connectionId,
      database: target.database,
      kind: target.kind,
      fromIdentifier: target.fromIdentifier,
      destination: target.destination,
      containingTable: target.containingTable,
      mysqlLowerCaseTableNames: target.mysqlLowerCaseTableNames,
      mssqlCatalogCaseSensitive: target.mssqlCatalogCaseSensitive,
      crossDatabase: tab.crossDatabase,
    });

    if (plan.status !== 'rewritten') {
      // Nothing provable here. Only warn when the buffer really does mention
      // the old name (a parse failure, or occurrences a gate refused) — a tab
      // that simply doesn't reference it stays silent.
      if (plan.status === 'parse-failed' || plan.skippedCount > 0) {
        notices.push(makeNotice(target, tab, 'stale', staleRanges(tab, plan.edits)));
      }
      continue;
    }

    const ranges = plan.edits.map((e) => e.range);
    const applied = applyToTab(tab, plan.edits, getEditor(tab.tabId), deps.setTabSql);
    if (applied === 'refused') {
      notices.push(makeNotice(target, tab, 'stale', ranges));
      continue;
    }
    rewrittenTabs += 1;
    notices.push({
      ...makeNotice(target, tab, 'rewritten', ranges),
      rewrittenCount: plan.edits.length,
      // Only the `executeEdits` route lands on the tab's own undo stack; the
      // store write reaches a mounted editor through `useQueryBufferSync`'s
      // `setValue`, which throws that stack away.
      undoable: applied === 'undoable',
      ...(plan.skippedCount > 0 ? { skippedCount: plan.skippedCount } : {}),
    });
  }

  return { notices, rewrittenTabs };
}

/** `undoable` — spliced through Monaco, restorable with one Ctrl+Z.
 *  `store`    — written through the workspace store; no undo entry exists.
 *  `refused`  — nothing was written (drifted model, or the store write threw). */
type ApplyOutcome = 'undoable' | 'store' | 'refused';

function applyToTab(
  tab: RewritableTab,
  edits: readonly BufferEdit[],
  editor: RenameBufferEditor | null,
  setTabSql: (tabId: string, sql: string) => void,
): ApplyOutcome {
  if (editor) {
    // The plan's byte offsets belong to `tab.sql`. If the live model has moved
    // on (the 200 ms `useQueryBufferSync` debounce, or a concurrent writer),
    // splicing them lands on the wrong bytes — refuse and let the stale notice
    // tell the user to check that buffer by hand. A store write is refused too:
    // it would clobber the newer text.
    const live = editor.getValue?.();
    if (live !== undefined && live !== tab.sql) return 'refused';
    if (
      editor.executeEdits?.(
        RENAME_TAB_UNDO_SOURCE,
        edits.map((e) => ({
          range: {
            startLineNumber: e.range.startLine + 1,
            startColumn: e.range.startColumn + 1,
            endLineNumber: e.range.endLine + 1,
            endColumn: e.range.endColumn + 1,
          },
          text: e.newText,
        })),
      )
    ) {
      return 'undoable';
    }
  }
  // No live editor (or the handle refused — Monaco chunk still loading): write
  // the rewritten text through the store instead. `useQueryBufferSync` seeds
  // from the store when the tab mounts, so the edit is picked up, not clobbered.
  try {
    setTabSql(tab.tabId, applyEditsToText(tab.sql, edits));
    return 'store';
  } catch {
    return 'refused';
  }
}

/** Right-to-left splice so earlier offsets stay valid. */
export function applyEditsToText(source: string, edits: readonly BufferEdit[]): string {
  const ordered = [...edits].sort((a, b) => b.range.startOffset - a.range.startOffset);
  let out = source;
  for (const edit of ordered) {
    out = out.slice(0, edit.range.startOffset) + edit.newText + out.slice(edit.range.endOffset);
  }
  return out;
}

function makeNotice(
  target: RewriteTarget,
  tab: RewritableTab,
  outcome: 'rewritten' | 'stale',
  references: readonly BufferEdit['range'][],
): TabCourtesyNotice {
  return {
    tabId: tab.tabId,
    oldName: target.fromIdentifier,
    newName: target.destination.identifier,
    kind: target.kind,
    references,
    outcome,
  };
}

/** A stale tab has no edit ranges to report; point the notice at the buffer
 *  start so the shape stays uniform for consumers that read `references`. */
function staleRanges(tab: RewritableTab, edits: readonly BufferEdit[]) {
  if (edits.length > 0) return edits.map((e) => e.range);
  return [rangeFromOffsets(buildLineIndex(tab.sql), 0, 0)];
}
